import { Navigate, Route, useLocation } from 'react-router-dom';

import {
  AdminParticipantCardRedirect,
  AdminParticipantsListRedirect,
  TEAM_PATHS
} from '@/domains/admin';
import {
  HOME_ROUTE,
  SIGN_IN_REQUEST_ROUTE,
  type IInvitationRequestRouteState
} from '@/domains/auth';
import { PORTAL_PATHS } from '@/domains/survey';

/**
 * Every address the app had before the two portals, redirected to its new place (the
 * two-portals design, §6), and one survey-api's mails name. This file is the only one that still
 * spells `/survey`, `/admin` or a Team Portal address that moved.
 *
 * **A prefix is rewritten on the raw path, never rebuilt from decoded params.** React Router hands
 * a route its params decoded; a user card's address can hold `/`, `%` or `.` (`account-detail`
 * spec), and rebuilding the new path from the decoded value would turn a `%2F` into a slash and
 * reach a different screen. So the redirect keeps the rest of the address exactly as it arrived —
 * still percent-encoded — and swaps only the prefix. Search, hash and history state go along
 * unchanged: a bookmarked search stays a search, and a link that carried the list's query in its
 * state still carries it.
 *
 * Mounted outside `ProtectedRoute`: a redirect needs no session, and one taken before sign-in
 * leaves the new address for the guard to save and return to.
 *
 * `/survey/add` and `/survey/teams/:participantId/routing` — the addresses the teams block gave
 * the old catalogue — go the same way, under `/portal/surveys`, since the prefix is all that is
 * rewritten.
 *
 * **`/my/cycles` is survey-api's address, not one this app ever had.** SR-416's reopen notice
 * (`AuthMailTemplates`) links `{WebBaseUrl}/my/cycles`, the API route's path, and the mails already
 * sent keep that link whatever survey-api changes later — so it goes to the catalogue, which is
 * what it meant (the roadmap's D6, Q7). Only the exact address: nothing under it was ever linked.
 *
 * **The password screens are retired** (SR-476): `/password/forgot` and `/password/reset` — the
 * address survey-api's reset mails linked — go to the request for an invitation, saying why, and
 * `/password/change` to `/`. A reset link's `#token=` is dropped with the address: nothing redeems
 * it any more.
 *
 * **The advisor's address change is retired** (SR-487): staff change a login address. `/email/change`
 * — the address survey-api's change mails linked — goes to the Account page, which says so; the
 * link's `#token=` is dropped with the address.
 *
 * **Team Portal addresses that moved** go the same way: Team firms and Firm lists, once under
 * Records, are Setup's Team routing (SR-513).
 *
 * The participant search and card that People replaced keep their own redirect components, which
 * translate the old query into People's (`peopleSearchOf`); they now land in the Team Portal. The
 * Team Portal study at `/admin/team-portal` is deleted, and its addresses go to the Team Portal's
 * home.
 */
export function legacyRoutes() {
  return (
    <>
      <Route path="/survey/*" element={<Rewrite from="/survey" to={PORTAL_PATHS.surveys} />} />
      <Route path="/my/cycles" element={<Rewrite from="/my/cycles" to={PORTAL_PATHS.surveys} />} />

      <Route path="/password/forgot" element={<PasswordsRetired />} />
      <Route path="/password/reset" element={<PasswordsRetired />} />
      <Route path="/password/change" element={<Navigate to={HOME_ROUTE} replace />} />
      <Route path="/email/change" element={<Navigate to={PORTAL_PATHS.account} replace />} />

      {/* SR-513: Team firms and Firm lists left Records for one Setup page, Team routing's Firms
          table — a tab's address finds its place in it; the firm lists are its Wires. */}
      <Route
        path="/team/team-firms/*"
        element={<Rewrite from="/team/team-firms" to={TEAM_PATHS.teamRouting} />}
      />
      <Route
        path="/team/firm-lists/*"
        element={<Navigate to={`${TEAM_PATHS.teamRouting}?route=wires`} replace />}
      />

      <Route path="/admin">
        <Route index element={<Rewrite from="/admin" to={TEAM_PATHS.home} />} />
        <Route
          path="responses/*"
          element={<Rewrite from="/admin/responses" to={TEAM_PATHS.responses} />}
        />
        <Route path="people/*" element={<Rewrite from="/admin/people" to={TEAM_PATHS.people} />} />
        <Route path="participants" element={<AdminParticipantsListRedirect />} />
        <Route path="participants/:participantId" element={<AdminParticipantCardRedirect />} />
        <Route
          path="registrations/*"
          element={<Rewrite from="/admin/registrations" to={TEAM_PATHS.registrations} />}
        />
        <Route path="sec-adv/*" element={<Rewrite from="/admin/sec-adv" to={TEAM_PATHS.secAdv} />} />
        <Route
          path="team-firms/*"
          element={<Rewrite from="/admin/team-firms" to={TEAM_PATHS.teamRouting} />}
        />
        <Route path="users/*" element={<Rewrite from="/admin/users" to={TEAM_PATHS.users} />} />
        <Route
          path="metadata/cycles/*"
          element={<Rewrite from="/admin/metadata/cycles" to={TEAM_PATHS.cycles} />}
        />
        <Route
          path="metadata/*"
          element={<Rewrite from="/admin/metadata" to={TEAM_PATHS.builder} />}
        />
        <Route path="*" element={<Navigate to={TEAM_PATHS.home} replace />} />
      </Route>
    </>
  );
}

/**
 * `from` + the rest → `to` + the same rest. The match is React Router's, which ignores case, so the
 * prefix is cut by its length rather than by its spelling.
 */
function Rewrite({ from, to }: { from: string; to: string }) {
  const location = useLocation();
  const rest = location.pathname.slice(from.length);
  return (
    <Navigate
      replace
      to={{ pathname: `${to}${rest}`, search: location.search, hash: location.hash }}
      state={location.state}
    />
  );
}

const PASSWORDS_RETIRED: IInvitationRequestRouteState = { passwordsRetired: true };

/** A retired password screen's address: the request for an invitation, which says why. */
function PasswordsRetired() {
  return <Navigate to={SIGN_IN_REQUEST_ROUTE} replace state={PASSWORDS_RETIRED} />;
}
